import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { Artist } from '../../types';
import { useLang } from '../../i18n/context';

export function SoundSystemsTab() {
  const { t, lang } = useLang();
  const [soundSystems, setSoundSystems] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);

  useEffect(() => { load(); }, [search]);

  async function load() {
    setLoading(true);
    try {
      const params: Record<string, string> = { type: 'soundsystem' };
      if (search) params.search = search;
      setSoundSystems(await api.getArtists(params));
    } catch (err) {
      console.error('Failed to load sound systems:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    try {
      const data = await api.getArtist(id);
      setDetail(data);
      setSelectedId(id);
    } catch (err) {
      console.error('Failed to load detail:', err);
    }
  }

  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div>
      <div className="section-header">
        <span className="section-title">&#128266; {t('soundsystems.title')}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{soundSystems.length} {t('soundsystems.followed')}</span>
      </div>

      <div className="filter-bar">
        <div className="search-wrapper">
          <span className="search-icon">&#128269;</span>
          <input
            className="search-input"
            placeholder={t('soundsystems.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>
      ) : soundSystems.length === 0 ? (
        <div className="empty-state"><p>{t('soundsystems.no_found')}</p></div>
      ) : (
        <div style={{ display: 'flex', gap: '16px' }}>
          {/* List */}
          <div style={{ flex: 1 }}>
            <div className="entity-grid">
              {soundSystems.map(ss => (
                <div key={ss.id} className="entity-card" style={{
                  cursor: 'pointer',
                  borderColor: selectedId === ss.id ? 'var(--gold)' : undefined,
                }} onClick={() => loadDetail(ss.id)}>
                  <div className="entity-name">&#128266; {ss.name}</div>
                  <div className="entity-meta">
                    {ss.country && <span>{ss.country}</span>}
                    {ss.genres?.length > 0 && <span> · {ss.genres.join(', ')}</span>}
                  </div>
                  <div className="genre-tags" style={{ marginTop: '8px' }}>
                    {ss.genres?.map(g => (
                      <span key={g} className={`genre-tag ${g.replace(/\s+/g, '-')}`}>{g}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                    {ss.instagram_url && <a href={ss.instagram_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" onClick={e => e.stopPropagation()}>&#128247; Instagram</a>}
                    {ss.facebook_url && <a href={ss.facebook_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" onClick={e => e.stopPropagation()}>&#128101; Facebook</a>}
                    {ss.website_url && <a href={ss.website_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm" onClick={e => e.stopPropagation()}>&#127760; {t('soundsystems.website')}</a>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          {detail && (
            <div style={{ width: '360px', flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
              <div className="entity-card" style={{ borderColor: 'var(--gold)', position: 'relative' }}>
                <button
                  onClick={() => { setDetail(null); setSelectedId(null); }}
                  style={{
                    position: 'absolute', top: '8px', right: '8px',
                    background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-color)',
                    borderRadius: '50%', width: '24px', height: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1,
                  }}
                  title={t('common.close')}
                >×</button>
                <div className="entity-name" style={{ fontSize: '18px', paddingRight: '28px' }}>&#128266; {detail.name}</div>
                <div className="entity-meta" style={{ marginBottom: '12px' }}>
                  {[detail.country, (detail.genres || []).join(', ')].filter(Boolean).join(' · ') || ''}
                </div>
                {detail.bio && <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>{detail.bio}</p>}

                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', color: 'var(--gold)', letterSpacing: '1px', textTransform: 'uppercase' as const }}>
                  {t('soundsystems.events_count')} ({detail.events?.length || 0})
                </div>
                {detail.events?.length > 0 ? (
                  detail.events.slice(0, 10).map((e: any) => (
                    <div key={e.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-color)', fontSize: '12px' }}>
                      <div style={{ color: new Date(e.date) >= new Date() ? 'var(--green)' : 'var(--text-muted)', fontFamily: 'JetBrains Mono', fontSize: '11px' }}>
                        {formatDate(e.date)} {new Date(e.date) >= new Date() ? `&#9679; ${t('agenda.upcoming')}` : ''}
                      </div>
                      <div style={{ fontWeight: 600 }}>{e.title}</div>
                      {e.venue_name && <div style={{ color: 'var(--text-muted)' }}>&#128205; {e.venue_name}</div>}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('soundsystems.no_events')}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
